import { Helmet } from "react-helmet-async";
import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, Link, Server, FileText, Loader2, List, Globe, Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import { cn } from "@/utils/functions/cn";
import { playlistApi, devicesApi } from "@/services/api";
import { toast } from "sonner";
import { t, type Lang } from '@/utils/i18n';
import { useLanguage } from '@/components/providers/language-provider';

function hexFromRaw(raw: string): string {
  return raw.replace(/[^a-fA-F0-9]/g, "").toUpperCase();
}

interface MacOtpInputProps {
  octets: string[];
  onChange: (octets: string[]) => void;
  disabled?: boolean;
}

const MacOtpInput = ({ octets, onChange, disabled }: MacOtpInputProps) => {
  const refs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null));

  const updateOctet = useCallback(
    (index: number, value: string) => {
      const next = [...octets];
      next[index] = hexFromRaw(value).slice(0, 2);
      onChange(next);
    },
    [octets, onChange]
  );

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const val = hexFromRaw(e.target.value).slice(0, 2);
    updateOctet(index, val);
    if (val.length === 2 && index < 5) {
      refs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && octets[index] === "" && index > 0) {
      refs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) refs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < 5) refs.current[index + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const hex = hexFromRaw(e.clipboardData.getData("text")).slice(0, 12);
    if (hex.length === 0) return;
    const next = Array.from({ length: 6 }, (_, i) => hex.slice(i * 2, i * 2 + 2));
    onChange(next);
    const lastFilled = next.reduce((acc: number, o: string, i: number) => o.length > 0 ? i : acc, -1);
    const focusIdx = Math.min(lastFilled + 1, 5);
    setTimeout(() => refs.current[focusIdx]?.focus(), 0);
  };

  const borderColor = (index: number) => {
    if (octets[index].length === 2) return "border-brand-accent/60 bg-brand-accent/[0.08]";
    return "border-white/[0.1] bg-white/[0.03]";
  };

  return (
    <div className="flex items-center gap-1 sm:gap-2 w-full max-w-full">
      {octets.map((val, i) => (
        <div key={i} className="flex items-center gap-1 sm:gap-2 min-w-0">
          <input
            ref={(el) => { refs.current[i] = el; }}
            type="text"
            inputMode="tel"
            autoComplete="off"
            spellCheck={false}
            maxLength={2}
            value={val}
            disabled={disabled}
            onChange={(e) => handleChange(i, e)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={handlePaste}
            onFocus={(e) => e.target.select()}
            className={cn(
              "w-10 h-12 sm:w-12 sm:h-14 rounded-[10px] sm:rounded-[12px] border-2 text-center font-mono text-sm sm:text-base font-semibold uppercase tracking-widest",
              "text-white outline-none transition-all duration-200",
              "focus:border-brand-accent focus:bg-brand-accent/[0.1] focus:shadow-[0_0_18px_rgba(68,138,255,0.25)]",
              "disabled:opacity-40 disabled:cursor-not-allowed",
              borderColor(i)
            )}
            placeholder=""
          />
          {i < 5 && (
            <span className="text-white/25 font-mono text-sm sm:text-lg font-light select-none">:</span>
          )}
        </div>
      ))}
    </div>
  );
};

type UploadMethod = 'url' | 'file' | 'server' | 'other';

const UploadPlaylistPage = () => {
  const { language } = useLanguage();

  const methods: { id: UploadMethod; label: string; icon: typeof Link }[] = [
    { id: 'url', label: t('playlist', 'urlTab', language), icon: Link },
    { id: 'file', label: t('playlist', 'fileTab', language), icon: Upload },
    { id: 'server', label: t('playlist', 'hostTab', language), icon: Server },
    { id: 'other', label: t('playlist', 'm3u8Tab', language), icon: FileText },
  ];

  const [octets, setOctets] = useState<string[]>(Array(6).fill(""));
  const macAddress = octets.join(":");
  const macHex = octets.join("");
  const isValidMac = octets.every((o) => o.length === 2) && macHex.length === 12;
  const [macStatus, setMacStatus] = useState<"idle" | "checking" | "found" | "not_found">("idle");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const macFound = macStatus === "found";

  useEffect(() => {
    if (!isValidMac) {
      setMacStatus("idle");
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    let cancelled = false;
    debounceRef.current = setTimeout(async () => {
      setMacStatus("checking");
      try {
        const res = await devicesApi.checkMacStatus(macAddress);
        if (cancelled) return;
        const device = (res as any).data?.device || res.device;
        setMacStatus(device ? "found" : "not_found");
      } catch {
        if (cancelled) return;
        setMacStatus("not_found");
      }
    }, 400);
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [macHex]);

  const [playlistName, setPlaylistName] = useState('');
  const [epgUrl, setEpgUrl] = useState('');
  const [pinProtection, setPinProtection] = useState(false);
  const [pin, setPin] = useState('');
  const [isDefault, setIsDefault] = useState(true);
  const [activeTab, setActiveTab] = useState<UploadMethod>('url');
  const [m3uUrl, setM3uUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [host, setHost] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [m3u8Url, setM3u8Url] = useState('');
  const [loading, setLoading] = useState(false);


  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.name.toLowerCase().endsWith('.m3u') || file.name.toLowerCase().endsWith('.m3u8')) {
        setSelectedFile(file);
        if (!playlistName) {
          setPlaylistName(file.name.replace(/\.(m3u|m3u8)$/i, ''));
        }
      } else {
        toast.error(t('playlist', 'fileError', language));
      }
    }
  };

  const isMethodValid = () => {
    switch (activeTab) {
      case 'url': return m3uUrl.trim() !== '';
      case 'file': return selectedFile !== null;
      case 'server': return host.trim() !== '' && username.trim() !== '' && password.trim() !== '';
      case 'other': return m3u8Url.trim() !== '';
      default: return false;
    }
  };

  const isFormValid = () => {
    return isValidMac && macFound &&
           playlistName.trim() !== '' &&
           (!pinProtection || pin.trim() !== '') &&
           isMethodValid();
  };

  const buildUrl = async (): Promise<string> => {
    switch (activeTab) {
      case 'url':
        return m3uUrl.trim();
      case 'file':
        return await selectedFile!.text();
      case 'server': {
        const cleanHost = host.trim().replace(/\/$/, '');
        return `${cleanHost}/get.php?username=${encodeURIComponent(username.trim())}&password=${encodeURIComponent(password.trim())}&type=m3u_plus&output=ts`;
      }
      case 'other':
        return m3u8Url.trim();
      default:
        throw new Error('Invalid method');
    }
  };

  const resetForm = () => {
    setOctets(Array(6).fill("")); setPlaylistName(''); setEpgUrl('');
    setPinProtection(false); setPin(''); setIsDefault(false);
    setM3uUrl(''); setSelectedFile(null);
    setHost(''); setUsername(''); setPassword('');
    setM3u8Url('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid()) return;

    setLoading(true);
    try {
      const url = await buildUrl();
      await playlistApi.webUpload({
        mac: macAddress.toLowerCase(),
        name: playlistName.trim(),
        url,
        epgUrl: epgUrl.trim() || undefined,
        pin: pinProtection ? pin : undefined,
        isProtected: pinProtection,
        isSelected: isDefault,
      });
      toast.success(t('playlist', 'uploadSuccess', language));
      resetForm();
    } catch (error: any) {
      toast.error(error?.message || t('playlist', 'uploadError', language));
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "text-sm bg-white/[0.04] border-white/[0.08] placeholder:text-white/20 focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-0 focus-visible:border-brand-accent rounded-[12px] h-11";

  return (
    <>
      <Helmet>
        <title>{t('playlist', 'title', language)} - MoovaTV</title>
        <meta name="description" content={t('playlist', 'subtitle', language)} />
      </Helmet>

      {/* One fold: title, steps and the three notes on the left; the whole upload form in one compact card on the right. */}
      <section className="px-4 sm:px-6 py-6 sm:py-8 lg:py-6 lg:min-h-[calc(100svh-var(--moova-header-height))] lg:flex lg:items-center">
        <div className="w-full max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] gap-5 sm:gap-7 lg:gap-16 items-center">

          {/* ── Left: title, steps, notes ── */}
          <div className="space-y-5 lg:space-y-7">
            <div>
              <h1 className="!text-[30px] sm:!text-[40px] lg:!text-[52px] font-semibold tracking-tight !leading-[1.08] !mt-0 !mb-2.5 sm:!mb-3 font-heading">
                {t('playlist', 'title', language)}
              </h1>
              <p className="text-[15px] sm:text-[17px] text-white/50 leading-relaxed max-w-md">
                {t('playlist', 'subtitle', language)}
              </p>
            </div>

            <ol className="flex flex-col gap-2.5 sm:gap-3 lg:gap-4">
              {[t('playlist', 'step1', language), t('playlist', 'step2', language), t('playlist', 'step3', language)].map((text, i) => (
                <li key={i} className="flex items-center gap-3 lg:gap-4">
                  <span className="h-8 w-8 lg:h-10 lg:w-10 shrink-0 grid place-items-center rounded-[9px] lg:rounded-[11px] bg-brand-accent/12 border border-brand-accent/25 text-[13px] lg:text-base font-semibold text-brand-accent">
                    {i + 1}
                  </span>
                  <span className="text-[14px] leading-snug text-white/70 lg:text-[16px] lg:text-white/75">{text}</span>
                </li>
              ))}
            </ol>

            <div className="hidden lg:grid grid-cols-3 gap-4">
              {[
                { icon: List, title: t('playlist', 'multipleFormats', language), text: t('playlist', 'multipleFormatsDesc', language) },
                { icon: Globe, title: t('playlist', 'epgSupport', language), text: t('playlist', 'epgSupportDesc', language) },
                { icon: Lock, title: t('playlist', 'pinProtection', language), text: t('playlist', 'pinProtectionDesc', language) },
              ].map(({ icon: Icon, title, text }) => (
                <div key={title} className="min-w-0">
                  <Icon className="h-4 w-4 text-brand-accent mb-2" />
                  <p className="text-[13px] font-medium leading-snug">{title}</p>
                  <p className="text-[12px] text-white/40 leading-snug mt-1">{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: form card ── */}
          <div className="rounded-[20px] border border-white/[0.08] bg-white/[0.035] p-4 sm:p-6 lg:p-7">
            <form onSubmit={handleSubmit} className="space-y-3.5 sm:space-y-4">
              {/* 1 · MAC */}
              <div className="space-y-2.5">
                <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t('playlist', 'macLabel', language)}</label>
                <MacOtpInput
                  octets={octets}
                  onChange={setOctets}
                />
                <div className="min-h-5">
                  {isValidMac && macStatus === "checking" && (
                    <div className="flex items-center gap-2 text-sm text-white/40">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>{t('playlist', 'checking', language)}</span>
                    </div>
                  )}
                  {isValidMac && macStatus === "found" && (
                    <div className="flex items-center gap-2 text-sm text-emerald-400">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span>{t('playlist', 'found', language)}</span>
                    </div>
                  )}
                  {isValidMac && macStatus === "not_found" && (
                    <div className="flex items-center gap-2 text-sm text-amber-400">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      <span>{t('playlist', 'notFound', language)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Rest of form — disabled until MAC is found */}
              <div className={cn("space-y-3.5 sm:space-y-4 transition-all duration-300", !macFound && "opacity-30 pointer-events-none select-none")}>

                {/* 2 · Name + method */}
                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t('playlist', 'playlistName', language)}</label>
                  <Input
                    id="playlist-name"
                    placeholder={t('playlist', 'playlistPlaceholder', language)}
                    value={playlistName}
                    onChange={(e) => setPlaylistName(e.target.value)}
                    className={inputClass}
                    disabled={!macFound}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t('playlist', 'uploadMethod', language)}</label>
                  <div className="grid grid-cols-4 gap-2">
                    {methods.map((m) => {
                      const Icon = m.icon;
                      const isActive = activeTab === m.id;
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setActiveTab(m.id)}
                          aria-pressed={isActive}
                          className={cn(
                            "flex items-center justify-center gap-1.5 h-10 px-1.5 sm:px-2 rounded-[10px] border transition-all duration-200 text-[11px] sm:text-xs",
                            isActive
                              ? "border-brand-accent bg-brand-accent/10 text-brand-accent"
                              : "border-white/[0.08] bg-white/[0.02] text-white/40 hover:bg-white/[0.04] hover:border-white/[0.12]"
                          )}
                        >
                          <Icon className="hidden sm:block h-3.5 w-3.5" />
                          <span className="truncate">{m.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  {activeTab === 'url' && (
                    <>
                      <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t('playlist', 'm3uUrl', language)}</label>
                      <Input
                        placeholder="https://example.com/playlist.m3u"
                        value={m3uUrl}
                        onChange={(e) => setM3uUrl(e.target.value)}
                        className={inputClass}
                      />
                    </>
                  )}

                  {activeTab === 'file' && (
                    <>
                      <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t('playlist', 'm3uFile', language)}</label>
                      <Input
                        type="file"
                        accept=".m3u,.m3u8"
                        onChange={handleFileChange}
                        className={cn(inputClass, "file:text-white/50 file:bg-transparent file:border-0 file:mr-3")}
                      />
                      {selectedFile && (
                        <div className="flex items-center gap-2 px-3 py-2 rounded-[10px] bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-400">
                          <FileText className="h-3.5 w-3.5" />
                          <span className="font-medium">{selectedFile.name}</span>
                          <span className="text-emerald-400/60">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
                        </div>
                      )}
                    </>
                  )}

                  {activeTab === 'server' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2 space-y-2">
                        <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t('playlist', 'hostLabel', language)}</label>
                        <Input
                          placeholder="http://example.com:8080"
                          value={host}
                          onChange={(e) => setHost(e.target.value)}
                          className={inputClass}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t('playlist', 'usernameLabel', language)}</label>
                        <Input
                          placeholder={t('playlist', 'usernameLabel', language).toLowerCase()}
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          className={inputClass}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t('playlist', 'passwordLabel', language)}</label>
                        <Input
                          type="password"
                          placeholder={t('playlist', 'passwordLabel', language).toLowerCase()}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className={inputClass}
                        />
                      </div>
                    </div>
                  )}

                  {activeTab === 'other' && (
                    <>
                      <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t('playlist', 'm3u8Url', language)}</label>
                      <Input
                        placeholder="https://example.com/playlist.m3u8"
                        value={m3u8Url}
                        onChange={(e) => setM3u8Url(e.target.value)}
                        className={inputClass}
                      />
                      <p className="text-[10px] text-white/25">{t('playlist', 'm3u8Note', language)}</p>
                    </>
                  )}
                </div>

                {/* 3 · Options: EPG beside the PIN / default toggles */}
                <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-3 sm:gap-4 items-end">
                  <div className="space-y-2">
                    <label className="block text-[11px] font-medium text-white/50 uppercase tracking-[0.12em]">{t('playlist', 'epgUrl', language)}</label>
                    <Input
                      placeholder="https://example.com/epg.xml"
                      value={epgUrl}
                      onChange={(e) => setEpgUrl(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                  <div className="flex flex-col gap-2.5 pb-1">
                    <div className="flex items-center gap-2.5">
                      <Checkbox
                        id="pin-protection"
                        checked={pinProtection}
                        onCheckedChange={(checked) => setPinProtection(checked === true)}
                        className="border-white/15 data-[state=checked]:bg-brand-accent data-[state=checked]:border-brand-accent"
                      />
                      <Label htmlFor="pin-protection" className="text-[13px] text-white/60">{t('playlist', 'protectPin', language)}</Label>
                      {pinProtection && (
                        <Input
                          type="password"
                          placeholder={t('playlist', 'pinPlaceholder', language)}
                          value={pin}
                          onChange={(e) => setPin(e.target.value)}
                          className={cn(inputClass, "h-9 w-[92px] text-center tracking-widest")}
                          maxLength={4}
                        />
                      )}
                    </div>
                    <div className="flex items-center gap-2.5">
                      <Checkbox
                        id="is-default"
                        checked={isDefault}
                        onCheckedChange={(checked) => setIsDefault(checked === true)}
                        className="border-white/15 data-[state=checked]:bg-brand-accent data-[state=checked]:border-brand-accent"
                      />
                      <Label htmlFor="is-default" className="text-[13px] text-white/60">{t('playlist', 'setDefault', language)}</Label>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className={cn(
                    "w-full h-12 sm:h-[52px] text-sm font-medium rounded-[14px] transition-all duration-200",
                    isFormValid()
                      ? "bg-brand-accent hover:bg-brand-accent/90 text-white shadow-[0_0_24px_rgba(68,138,255,0.25)] hover:shadow-[0_0_32px_rgba(68,138,255,0.35)]"
                      : "bg-white/[0.06] text-white/25 cursor-not-allowed"
                  )}
                  disabled={!isFormValid() || loading}
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('playlist', 'uploading', language)}</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" />{t('playlist', 'uploadBtn', language)}</>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
        </div>
      </section>
    </>
  );
};

export default UploadPlaylistPage;
